import { type ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'
import { Button } from '~/shadcn/ui/button/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~/shadcn/ui/dropdown-menu'
import { cn } from '~/shadcn/lib/utils'

export type MultiselectOption<TValue> = { value: TValue; label: ReactNode }

type Props<TValue> = {
  disabled?: boolean
  label: string
  values?: TValue[]
  options: MultiselectOption<TValue>[]
  colorScheme?: 'dark' | 'light'
  onChange(value: TValue[]): void
  className?: string
}

export function MultiSelect<
  TValue extends string | number | symbol | Record<string, any>,
>({
  disabled,
  label,
  colorScheme = 'dark',
  values: selectedValues = [],
  options,
  onChange,
  className,
}: Props<TValue>) {
  const someSelected = selectedValues.length > 0
  const isDark = colorScheme === 'dark'

  const handleSelect = (value: TValue) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value]
    onChange(newValues)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="filter"
          size="filter"
          className={cn(
            'max-w-fit rounded-lg px-3 py-2',
            'border-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            someSelected
              ? 'border-accent bg-accent hover:bg-accent text-primary-foreground'
              : isDark
                ? 'border-border text-primary-foreground'
                : 'border-border-strong text-primary',
            className
          )}>
          <div className="flex items-center justify-between gap-1">
            {label}
            <ChevronDown className="h-6 w-6" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="mt-0.5 rounded-lg border-2 bg-sidebar border-border-strong p-1 font-semibold text-primary-foreground"
        align="start">
        {options.map((option, index) => (
          <DropdownMenuCheckboxItem
            key={index}
            checked={selectedValues.includes(option.value)}
            onSelect={(e) => {
              e.preventDefault()
              handleSelect(option.value)
            }}
            className="cursor-pointer rounded-lg p-3 pr-8">
            <div className="flex w-full items-center justify-between gap-2">
              {option.label}
            </div>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
